if (!customElements.get('glysium-editorial')) {
  customElements.define('glysium-editorial', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abortController = new AbortController();
      this.frameIds = new Set();
      const signal = this.abortController.signal;
      const scope = this.closest('#MainContent') || this.closest('main') || document;
      const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      const nextFrame = callback => {
        const id = requestAnimationFrame(() => {
          this.frameIds.delete(id);
          if (!signal.aborted && this.isConnected) callback();
        });
        this.frameIds.add(id);
      };
      const isPlainClick = event => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      const thumbnails = Array.from(this.querySelectorAll('[data-ge-target]'));
      const slides = Array.from(this.querySelectorAll('[data-ge-slide]'));
      const selectImage = button => {
        const selected = slides.find(slide => slide.id === button.dataset.geTarget);
        if (!selected) return;
        slides.forEach(slide => {
          slide.hidden = slide !== selected;
          if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
        });
        thumbnails.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        const counter = this.querySelector('[data-ge-index]');
        if (counter) counter.textContent = String(thumbnails.indexOf(button) + 1).padStart(2, '0');
      };
      thumbnails.forEach((button, index) => {
        button.addEventListener('click', () => selectImage(button), { signal });
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? thumbnails.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + thumbnails.length) % thumbnails.length;
          thumbnails[next].focus();
          selectImage(thumbnails[next]);
        }, { signal });
      });
      const dialog = this.querySelector('.ge-lightbox');
      const largeImage = dialog?.querySelector('[data-ge-large]');
      let zoomTrigger = null;
      this.querySelectorAll('[data-ge-zoom]').forEach(link => {
        link.addEventListener('click', event => {
          if (!isPlainClick(event) || !dialog?.showModal || !largeImage) return;
          const sourceImage = link.querySelector('img');
          largeImage.src = link.href;
          largeImage.alt = sourceImage?.alt || this.querySelector('.ge-title, h1')?.textContent?.trim() || '';
          try {
            if (!dialog.open) dialog.showModal();
            zoomTrigger = link;
            event.preventDefault();
          } catch (_) {}
        }, { signal });
      });
      const closeDialog = () => {
        if (dialog?.open) dialog.close();
      };
      dialog?.querySelector('.ge-close')?.addEventListener('click', closeDialog, { signal });
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog();
      }, { signal });
      dialog?.addEventListener('close', () => {
        if (zoomTrigger?.isConnected) zoomTrigger.focus({ preventScroll: true });
      }, { signal });
      const selectHotspot = button => {
        const group = button.closest('[data-ge-hotspots]');
        if (!group || !scope.contains(group)) return;
        const buttons = Array.from(group.querySelectorAll('[data-ge-hotspot]'));
        const panels = Array.from(group.querySelectorAll('[data-ge-panel]'));
        const panel = panels.find(item => item.id === button.getAttribute('aria-controls'));
        if (!panel) return;
        buttons.forEach(item => item.setAttribute('aria-expanded', String(item === button)));
        panels.forEach(item => { item.hidden = item !== panel; });
      };
      scope.addEventListener('click', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-ge-hotspot]') : null;
        if (button) selectHotspot(button);
      }, { signal });
      scope.addEventListener('keydown', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-ge-hotspot]') : null;
        if (!button || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        const group = button.closest('[data-ge-hotspots]');
        if (!group) return;
        const buttons = Array.from(group.querySelectorAll('[data-ge-hotspot]'));
        const index = buttons.indexOf(button);
        if (index < 0) return;
        event.preventDefault();
        const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (forward ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next].focus({ preventScroll: true });
        selectHotspot(buttons[next]);
      }, { signal });
      const targetSelectors = { finishes: '.gfp__finishes', accessories: '.gfp__accessories', technical: '.gfp__technical' };
      const jumpLinks = Array.from(this.querySelectorAll('[data-ge-jump]'));
      const getDestination = link => {
        const selector = targetSelectors[link.dataset.geJump];
        return selector ? scope.querySelector(selector) : null;
      };
      const prepareJumpLinks = () => {
        jumpLinks.forEach(link => {
          const target = getDestination(link);
          if (!target) return;
          const preferredId = `ge-${link.dataset.geJump}`;
          if (!target.id) target.id = preferredId;
          const preferred = document.getElementById(preferredId);
          link.setAttribute('href', `#${preferred && scope.contains(preferred) ? preferredId : target.id}`);
        });
      };
      const openDetails = destination => {
        let current = destination;
        while (current && current !== scope) {
          if (current.tagName === 'DETAILS') current.open = true;
          current = current.parentElement;
        }
      };
      const hashValue = () => {
        try { return decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return ''; }
      };
      const openHashDestination = () => {
        const hash = hashValue();
        if (!hash) return null;
        const link = jumpLinks.find(item => item.hash.slice(1) === hash);
        const destination = link ? getDestination(link) : null;
        if (destination) openDetails(destination);
        return destination;
      };
      jumpLinks.forEach(link => {
        link.addEventListener('click', event => {
          if (!isPlainClick(event)) return;
          prepareJumpLinks();
          const destination = getDestination(link);
          if (!destination) return;
          event.preventDefault();
          openDetails(destination);
          const nextUrl = new URL(link.href, window.location.href);
          if (window.location.hash !== nextUrl.hash) {
            try {
              window.history.pushState(window.history.state, '', nextUrl.href);
            } catch (_) {
              window.location.hash = nextUrl.hash;
            }
          }
          destination.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }, { signal });
      });
      window.addEventListener('hashchange', openHashDestination, { signal });
      window.addEventListener('popstate', openHashDestination, { signal });
      const bar = this.querySelector('[data-ge-actionbar]');
      let heroActions = null;
      let toolbars = [];
      this.bodyStyleChanges = new Map();
      const setBodyProperty = (name, value) => {
        let change = this.bodyStyleChanges.get(name);
        if (!change) {
          change = { value: document.body.style.getPropertyValue(name), priority: document.body.style.getPropertyPriority(name), applied: '' };
          this.bodyStyleChanges.set(name, change);
        }
        if (change.applied === value) return;
        document.body.style.setProperty(name, value);
        change.applied = value;
      };
      const updateBarMeasurements = () => {
        if (!bar) return;
        let bottomOffset = 0;
        toolbars.forEach(toolbar => {
          const style = getComputedStyle(toolbar);
          const bounds = toolbar.getBoundingClientRect();
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0 || bounds.width <= 0 || bounds.height <= 0 || bounds.top >= window.innerHeight || bounds.bottom <= 0) return;
          bottomOffset = Math.max(bottomOffset, window.innerHeight - Math.max(bounds.top, 0));
        });
        setBodyProperty('--ge-bottom-offset', `${Math.ceil(bottomOffset)}px`);
        if (!bar.hidden) {
          const height = Math.ceil(bar.getBoundingClientRect().height);
          if (height > 0) setBodyProperty('--ge-actionbar-space', `calc(${height}px - env(safe-area-inset-bottom, 0px))`);
        }
      };
      const updateActionbar = () => {
        if (!bar) return;
        const show = Boolean(heroActions?.isConnected && heroActions.getBoundingClientRect().bottom <= 0);
        if (bar.hidden !== !show && !show && bar.contains(document.activeElement)) {
          const focused = document.activeElement;
          const matching = heroActions?.querySelector(focused.classList.contains('ge-secondary') ? '.ge-secondary' : '.ge-primary');
          matching?.focus({ preventScroll: true });
        }
        bar.hidden = !show;
        updateBarMeasurements();
      };
      const observeActions = () => {
        const current = this.querySelector('.ge-info .ge-actions');
        if (heroActions !== current) {
          this.actionObserver?.disconnect();
          heroActions = current;
          if (heroActions && this.actionObserver) this.actionObserver.observe(heroActions);
        }
        toolbars = Array.from(document.querySelectorAll('.hdt-navbar-mobile'));
        this.layoutObserver?.disconnect();
        if (this.layoutObserver) {
          this.layoutObserver.observe(bar);
          toolbars.forEach(toolbar => this.layoutObserver.observe(toolbar));
        }
        updateActionbar();
      };
      if (bar) {
        bar.hidden = true;
        if ('IntersectionObserver' in window) {
          this.actionObserver = new IntersectionObserver(updateActionbar, { threshold: 0 });
        }
        if ('ResizeObserver' in window) this.layoutObserver = new ResizeObserver(updateBarMeasurements);
        let scrollFrameQueued = false;
        window.addEventListener('scroll', () => {
          if (scrollFrameQueued) return;
          scrollFrameQueued = true;
          nextFrame(() => { scrollFrameQueued = false; updateActionbar(); });
        }, { passive: true, signal });
        window.addEventListener('resize', updateActionbar, { passive: true, signal });
        window.addEventListener('pageshow', updateActionbar, { signal });
      }
      const refresh = () => {
        prepareJumpLinks();
        observeActions();
      };
      document.addEventListener('shopify:section:load', refresh, { signal });
      refresh();
      const initialDestination = openHashDestination();
      if (initialDestination) nextFrame(() => initialDestination.scrollIntoView({ behavior: 'auto', block: 'start' }));
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.actionObserver?.disconnect();
      this.layoutObserver?.disconnect();
      this.frameIds?.forEach(id => cancelAnimationFrame(id));
      this.frameIds?.clear();
      const bar = this.querySelector('[data-ge-actionbar]');
      if (bar) bar.hidden = true;
      const dialog = this.querySelector('.ge-lightbox');
      if (dialog?.open) dialog.close();
      this.bodyStyleChanges?.forEach((change, name) => {
        if (document.body.style.getPropertyValue(name) !== change.applied) return;
        if (change.value) document.body.style.setProperty(name, change.value, change.priority);
        else document.body.style.removeProperty(name);
      });
      this.bodyStyleChanges?.clear();
      this.ready = false;
    }
  });
}
